// @flow

import { jitsiLocalStorage } from '@jitsi/js-utils';

import VideoLayout from '../../../../../modules/UI/videolayout/VideoLayout';
import {
    setCustomParticipantVisible,
    setObjectFit
} from '../../../base/conference';
import { addTileGradients, isDeviceLandScape } from '../../../base/environment/environment.js';
import { getWindowWidth } from '../../../base/environment/utils';
import {
    pinParticipant,
    setParticipantColor
} from '../../../base/participants';
import { getBrandingColorPalette } from '../../../base/styles';
import { setHideHeaderFooter } from '../../../filmstrip/actions.native.js';
import { setTileLimitTileView } from '../../../filmstrip/actions.web.js';
import { setLargeVideoFullscreen } from '../../../large-video/actions.any';
import { close, open } from '../../../participants-pane/actions.js';
import {
    setLabelsVisible
} from '../../../toolbox/actions.web';
import { LAYOUTS, setTileView } from '../../../video-layout';
import { CONFERENCE_SUBJECT } from '../../actionTypes.js';
import { conferenceViewHandler } from '../../actions.web';
import {
    handleIframeSharingStartFilmstrip,
    handleIframeSharingStopFilmstrip,
    handleParticipantCountFilmstrip,
    handlePipFilmstripTransition,
    handleResponsiveLayoutCheck,
    handleScreenSharingStartFilmstrip,
    handleScreenSharingStopFilmstrip,
    onPresentationSharingStopped
} from '../../filmStripViewManager';
import { hasActivePriorityContent } from '../../recorderViewHelpers';

declare var interfaceConfig: Object;
declare var config: Object;

/**
 * Handles the full componentDidUpdate logic for the Conference component.
 * Called from Conference.componentDidUpdate(prevProps).
 *
 * @param {Object} instance - The Conference class instance.
 * @param {Object} prevProps - The previous props.
 * @returns {void}
 */
export function handleConferenceUpdate(instance: Object, prevProps: Object) {
    const {
        _participants,
        _participantCount,
        _conferenceView,
        _participantsColorUndifined,
        whiteBoardVisible,
        etherPadVisible,
        fakeParticipantsLength,
        _isPictureInPictureEnabled,
        dispatch,
        _objectFit,
        _screenSharingParticipantId,
        _mhConferenceJoined,
        _isScreenSharing,
        participantSharingVideo,
        filmStripView,
        _isSharedVideoVisible,
        _showPrejoin,
        _isLoading,
        hideHeaderFooter,
        largevideofullscreen
    } = instance.props;

    const shouldAutoRefreshOnLoaderError
        = !config.prejoinPageEnabled
        && !_showPrejoin
        && _mhConferenceJoined
        && !_isLoading
        && Boolean(instance.props._joinErrorMessage);
    const hadAutoRefreshCondition
        = !config.prejoinPageEnabled
        && !prevProps._showPrejoin
        && prevProps._mhConferenceJoined
        && !prevProps._isLoading
        && Boolean(prevProps._joinErrorMessage);

    if (shouldAutoRefreshOnLoaderError && !hadAutoRefreshCondition) {
        clearTimeout(instance.loaderErrorRefreshTimer);
        instance.loaderErrorRefreshTimer = setTimeout(() => {
            window.location.reload();
        }, 6000);
    } else if (!shouldAutoRefreshOnLoaderError && hadAutoRefreshCondition) {
        clearTimeout(instance.loaderErrorRefreshTimer);
        instance.loaderErrorRefreshTimer = null;
    }

    const isMobile = getWindowWidth() < 500;
    const isIframeSharing = whiteBoardVisible || etherPadVisible;
    const wasIframeSharing = prevProps.whiteBoardVisible || prevProps.etherPadVisible;

    // Detect what changed
    const screenSharingStarted = _isScreenSharing && !prevProps._isScreenSharing;
    const screenSharingStopped = !_isScreenSharing && prevProps._isScreenSharing;
    const screenSharingParticipantChanged = _isScreenSharing && _screenSharingParticipantId
        && _screenSharingParticipantId !== prevProps._screenSharingParticipantId;

    const iframeSharingStarted = isIframeSharing && !wasIframeSharing;
    const iframeShareingStopped = !isIframeSharing && wasIframeSharing;
    const sharedVideoStopped = !_isSharedVideoVisible && prevProps._isSharedVideoVisible;

    const isTileFilmstrip = filmStripView === LAYOUTS.TILE_VIEW;
    let isTile = isTileFilmstrip;

    if (_isPictureInPictureEnabled && instance.props._isParticipantsPaneVisible && !instance.pip_participants_pane) {
        dispatch(close());
        instance.pip_participants_pane = true;
    } else if (!_isPictureInPictureEnabled && !instance.props._isParticipantsPaneVisible && instance.pip_participants_pane) {
        dispatch(open());
        instance.pip_participants_pane = false;
    }

    if (!prevProps._room && instance.props._room && instance.props._isModerator) {
        instance.props._room.sendCommand(CONFERENCE_SUBJECT, {
            value: instance.props._subject
        });
    }

    if (document && !_mhConferenceJoined) {
        if (document.body) {
            const googleCaptcha = document.getElementById('googleRecaptcha');
            const googleRec = document.querySelector('.grecaptcha-badge');
            const captcha = document.querySelector('.rc-anchor');

            if (captcha) {
                captcha.remove();
            }
            if (googleCaptcha) {
                googleCaptcha.remove();
            }
            if (googleRec) {
                googleRec.remove();
            }
        }
    }

    handleResponsiveLayoutCheck(dispatch, filmStripView, _isPictureInPictureEnabled);

    if (isDeviceLandScape()) {
        if (!instance._hasAutoHideHeaderFooter) {
            instance._hasAutoHideHeaderFooter = true;
            if (!hideHeaderFooter) {
                dispatch(setHideHeaderFooter(true));
            }
        }
    } else {
        instance._hasAutoHideHeaderFooter = false;
    }

    if (fakeParticipantsLength > 4 && instance.props.tileViewTileLimit === 5) {
        dispatch(setTileLimitTileView(7));
    }

    if (participantSharingVideo && participantSharingVideo !== prevProps.participantSharingVideo) {
        dispatch(setCustomParticipantVisible(false));
        dispatch(setLabelsVisible(false));
    }

    if (config.iAmRecorder && _objectFit === 'contain' && !_isScreenSharing) {
        dispatch(setObjectFit('cover'));
    }

    if (prevProps._isPictureInPictureEnabled !== _isPictureInPictureEnabled) {
        handlePipFilmstripTransition(
            dispatch,
            instance,
            _isPictureInPictureEnabled,
            filmStripView
        );
        if (_isPictureInPictureEnabled) {
            dispatch(conferenceViewHandler('fixed'));
        } else {
            const width = getWindowWidth();

            if (width > 500 && largevideofullscreen) {
                dispatch(setLargeVideoFullscreen(false));
            }

            const savedView = jitsiLocalStorage.getItem('conferenceView') || 'fixed';

            dispatch(conferenceViewHandler(savedView));
        }

        // Refresh video layout to apply current tileViewTileLimit when PiP mode changes
        VideoLayout.sortThumbnails();
        VideoLayout.updateAllVideos();
    }

    // Handle tileViewTileLimit changes - refresh video layout to apply new limit
    if (prevProps.tileViewTileLimit !== instance.props.tileViewTileLimit) {
        // Force immediate refresh when in PIP mode to ensure changes are visible
        if (_isPictureInPictureEnabled) {
            // Clear any pending sort operations to force immediate execution
            VideoLayout.sortThumbnails();

            // Use setTimeout to ensure DOM updates are processed
            setTimeout(() => {
                VideoLayout.sortThumbnails();
                VideoLayout.updateAllVideos();
            }, 0);
        } else {
            VideoLayout.sortThumbnails();
            VideoLayout.updateAllVideos();
        }
    }

    // Initialize sharing state if not exists
    if (!instance.state.sharingState) {
        instance.setState({
            sharingState: {
                lastShareType: null, // 'screenshare' | 'iframe' | null
                lastShareTimestamp: 0,
                savedFilmstrip: null,
                savedObjectFit: null
            }
        });

        return;
    }

    if (iframeSharingStarted) {
        instance.setState(prevState => {
            const { sharingState } = prevState;

            return {
                sharingState: {
                    ...sharingState,
                    savedFilmstrip: sharingState.savedFilmstrip ?? filmStripView,
                    savedObjectFit: sharingState.savedObjectFit ?? _objectFit,
                    lastShareType: 'iframe',
                    lastShareTimestamp: Date.now()
                }
            };
        });

        handleIframeSharingStartFilmstrip(dispatch, instance, filmStripView, _isPictureInPictureEnabled);

        dispatch(pinParticipant(null));
        dispatch(setCustomParticipantVisible(true));
        dispatch(setLabelsVisible(false));
    } else if (screenSharingStarted || screenSharingParticipantChanged) {
        const currentLayout = filmStripView === LAYOUTS.TILE_VIEW || instance.props._shouldDisplayTileView
            ? LAYOUTS.TILE_VIEW
            : filmStripView;

        instance.setState(prevState => {
            const { sharingState } = prevState;

            return {
                sharingState: {
                    ...sharingState,
                    savedFilmstrip: sharingState.savedFilmstrip ?? currentLayout,
                    savedObjectFit: sharingState.savedObjectFit ?? _objectFit,
                    lastShareType: 'screenshare',
                    lastShareTimestamp: Date.now()
                }
            };
        });

        handleScreenSharingStartFilmstrip(dispatch, instance, filmStripView, _isPictureInPictureEnabled, screenSharingStarted);

        dispatch(setCustomParticipantVisible(false));

        // Auto-pinning the screenshare participant is now handled centrally by the
        // priority-content orchestrator (features/video-layout), which pins the most
        // recent presentation while respecting any manual or moderator pin.
        dispatch(setLabelsVisible(false));
    } else if (iframeShareingStopped) {
        dispatch(setCustomParticipantVisible(false));

        if (_isScreenSharing && _screenSharingParticipantId) {
            // Switch to screenshare
            instance.setState(prevState => {
                return {
                    sharingState: {
                        ...prevState.sharingState,
                        lastShareType: 'screenshare',
                        lastShareTimestamp: Date.now()
                    }
                };
            });

            // Auto-pinning the screenshare participant is now handled centrally by
            // the priority-content orchestrator (features/video-layout).
            dispatch(setLabelsVisible(false));
        } else {
            const restoredTile = handleIframeSharingStopFilmstrip(
                dispatch,
                instance,
                instance.state.sharingState,
                _isPictureInPictureEnabled
            );

            if (restoredTile) {
                isTile = true;
            }

            const { savedObjectFit } = instance.state.sharingState;

            if (savedObjectFit) {
                dispatch(setObjectFit(savedObjectFit));
            }

            // Only unpin if no other priority content is active for the recorder
            if (!config.iAmRecorder || !hasActivePriorityContent(APP.store.getState())) {
                dispatch(pinParticipant(null));
            }
            dispatch(setLabelsVisible(true));

            instance.setState({
                sharingState: {
                    lastShareType: null,
                    lastShareTimestamp: 0,
                    savedFilmstrip: null,
                    savedObjectFit: null
                }
            });
        }
    } else if (screenSharingStopped) {
        if (isIframeSharing) {
            // Switch to iframe
            instance.setState(prevState => {
                return {
                    sharingState: {
                        ...prevState.sharingState,
                        lastShareType: 'iframe',
                        lastShareTimestamp: Date.now()
                    }
                };
            });

            dispatch(setCustomParticipantVisible(true));
            dispatch(pinParticipant(null));
            dispatch(setLabelsVisible(false));
        } else {
            const restoredTile = handleScreenSharingStopFilmstrip(
                dispatch,
                instance,
                instance.state.sharingState,
                _isPictureInPictureEnabled
            );

            if (restoredTile) {
                isTile = true;
            }

            const { savedObjectFit } = instance.state.sharingState;

            if (savedObjectFit) {
                dispatch(setObjectFit(savedObjectFit));
            }

            // Only unpin if no other priority content is active for the recorder
            if (!config.iAmRecorder || !hasActivePriorityContent(APP.store.getState())) {
                dispatch(pinParticipant(null));
            }
            dispatch(setLabelsVisible(true));

            instance.setState({
                sharingState: {
                    lastShareType: null,
                    lastShareTimestamp: 0,
                    savedFilmstrip: null,
                    savedObjectFit: null
                }
            });
        }
    } else if (sharedVideoStopped && !_isScreenSharing && !isIframeSharing) {
        const restoredTile = onPresentationSharingStopped(dispatch);

        if (restoredTile) {
            isTile = true;
        }
    }


    // =============================================================
    // PARTICIPANT COUNT LOGIC (independent)
    // =============================================================

    handleParticipantCountFilmstrip(dispatch, {
        participantCount: _participantCount,
        prevParticipantCount: prevProps._participantCount,
        filmStripView,
        isPiPEnabled: _isPictureInPictureEnabled,
        isIframeSharing,
        isScreenSharing: _isScreenSharing,
        isSharedVideoVisible: _isSharedVideoVisible
    });

    // =============================================================
    // RECORDER SPECIFIC LOGIC
    // =============================================================

    if ((config?.iAmRecorder || interfaceConfig.applyMeetingSettings) && getBrandingColorPalette(interfaceConfig)?.tileBackground !== 'multiple') {
        _participants.forEach(participant => {
            if (participant.color !== getBrandingColorPalette(interfaceConfig)?.tileBackground) {
                dispatch(setParticipantColor(participant.id, getBrandingColorPalette(interfaceConfig)?.tileBackground));
            }
        });
    }
    if (
        getBrandingColorPalette(interfaceConfig)?.tileBackground === 'multiple'
        && (config?.iAmRecorder === true || interfaceConfig.applyMeetingSettings)
        && _participantsColorUndifined !== 0
    ) {
        _participants.forEach(p => {
            if (!p.color) {
                const randomHue = Math.floor(Math.random() * 360);
                const hue = (randomHue + 137.508) % 360;
                const saturation = 39;
                const lightness = 30;

                // eslint-disable-next-line max-len
                const tileBackground = `hsl(${hue} ${saturation} ${lightness})`;

                dispatch(setParticipantColor(p.id, addTileGradients(tileBackground)));
            }
        });
    }

    if (config.iAmRecorder && _conferenceView === 'fixed' && !instance.state.recorderinfloat) {
        dispatch(conferenceViewHandler('float'));
        instance.setState({ recorderinfloat: true });
    }
    if (_conferenceView === 'float' && window.innerWidth < 805 && !config.iAmRecorder) {
        dispatch(conferenceViewHandler('fixed'));
    }


    isTile
        ? dispatch(setTileView(true))
        : dispatch(setTileView(false));


    if (isMobile) {
        dispatch(conferenceViewHandler('fixed'));
    }

    instance._syncAdScheduler(prevProps);

    if (
        instance.props._shouldDisplayTileView === prevProps._shouldDisplayTileView
    ) {
        return;
    }

    // TODO: For now VideoLayout is being called as LargeVideo and Filmstrip
    // sizing logic is still handled outside of React. Once all components
    // are in react they should calculate size on their own as much as
    // possible and pass down sizings.
    VideoLayout.refreshLayout();
}
